<template>
  <dialog-layout
    title="Edit location"
    :fullscreen="fullscreen"
    :navIcon="navIcon"
    :dark="dark"
    @pop-dialog="$emit('pop-dialog')"
  >
    <v-container>
      <v-text-field
        ref="nameInput"
        v-model="localName"
        label="Location name"
        variant="underlined"
        autocomplete="off"
        spellcheck="false"
        class="mb-2"
        @keydown.enter="onEnter"
      />

      <v-number-input
        v-model="localNumberOfTables"
        label="Number of tables"
        variant="outlined"
        control-variant="stacked"
        style="width: 200px"
        :min="1"
        class="mt-4"
      />

      <div class="d-flex align-center mb-6 mt-6">
        <p class="text-body-2 text-medium-emphasis mb-0 mt-0">
          Time slots
        </p>
        <v-tooltip location="top">
          <template v-slot:activator="{ props }">
            <span v-bind="props" class="d-flex align-center">
              <v-icon icon="mdi-information-outline" size="small" class="ml-1" />
            </span>
          </template>
          <span>The recurring day and time this location hosts games.</span>
        </v-tooltip>
      </div>
      <div v-for="(slot, index) in localSlots" :key="index" class="d-flex align-center ga-2 mb-2">
        <v-select
          v-model="slot.dayOfWeek"
          label="Day"
          variant="outlined"
          density="compact"
          autocomplete="off"
          spellcheck="false"
          class="mb-0"
          :items="weekdayOptions"
          style="flex: 1 1 0"
        />
        <v-menu
          v-model="slot.menu"
          class="dialog__menu"
          :close-on-content-click="false"
          min-width="0"
        >
          <template v-slot:activator="{ props }">
            <v-text-field
              :model-value="format24to12(slot.time)"
              label="Time"
              variant="outlined"
              density="compact"
              autocomplete="off"
              spellcheck="false"
              readonly
              class="mb-0"
              style="flex: 1 1 0"
              v-bind="props"
            />
          </template>
          <v-time-picker v-model="slot.time">
            <template v-slot:actions>
              <v-spacer />
              <v-btn
                variant="flat"
                color="primary"
                :disabled="!slot.time"
                @click="slot.menu = false"
              >
                OK
              </v-btn>
            </template>
          </v-time-picker>
        </v-menu>
        <v-btn icon variant="text" size="small" style="margin-top: -27px;" @click="removeSlot(index)">
          <v-icon icon="mdi-delete" size="small" />
        </v-btn>
      </div>
      <v-btn
        variant="tonal"
        prepend-icon="mdi-plus"
        class="mt-1"
        @click="addSlot"
      >
        Add time slot
      </v-btn>

      <div class="d-flex justify-end mt-16">
        <v-btn class="mr-2" @click="$emit('pop-dialog')">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="saving || !localName.trim() || localSlots.length === 0 || !hasChanges"
          @click="save"
        >
          Save
        </v-btn>
      </div>
    </v-container>
  </dialog-layout>
</template>

<script>
import { DialogLayout } from "vue-app-components";
import { weekdayNames, clockToMinutes, minutesToHHMM, format24to12 } from "@/utils/time";

function emptySlot () {
  return { dayOfWeek: 0, time: "19:00", menu: false };
}

export default {
  name: "dialog-locations",
  components: {
    DialogLayout
  },
  props: {
    fullscreen: {
      type: Boolean,
      required: true
    },
    navIcon: {
      type: String,
      required: true
    },
    dark: {
      type: Boolean,
      default: false
    }
  },
  inject: ["$store"],
  data () {
    return {
      localName: "",
      localNumberOfTables: 1,
      localSlots: [],
      saving: false,
      location: null
    };
  },
  created () {
    const id = this.$dialog.getId();
    if (!id) return;
    const handler = (locations) => {
      if (!locations) return;
      const location = locations.find(l => l.id === id);
      if (location) {
        this.location = location;
        this.localName = location.name === "Untitled" ? "" : location.name;
        if (!this.localName) {
          this.$nextTick(() => this.$refs?.nameInput?.focus());
        }
        this.localNumberOfTables = location.numberOfTables;
        this.localSlots = location.slots.length > 0
          ? location.slots.map(slot => ({
            dayOfWeek: slot.dayOfWeek,
            time: minutesToHHMM(slot.time),
            menu: false
          }))
          : [emptySlot()];
        unwatch();
      }
    };
    const unwatch = this.$watch(() => this.$store.league?.locations, handler);
    handler(this.$store.league?.locations);
  },
  computed: {
    weekdayOptions () {
      return weekdayNames.map((name, index) => ({
        title: name,
        value: index
      }));
    },
    hasChanges () {
      if (!this.location) return false;
      const nameChanged = this.localName.trim() !== (this.location.name === "Untitled" ? "" : this.location.name);
      const tablesChanged = this.localNumberOfTables !== this.location.numberOfTables;
      const currentSlots = this.localSlots
        .filter(s => s.dayOfWeek != null && s.time)
        .map(s => `${s.dayOfWeek}:${clockToMinutes(s.time)}`)
        .sort()
        .join(",");
      const originalSlots = this.location.slots
        .map(s => `${s.dayOfWeek}:${s.time}`)
        .sort()
        .join(",");
      return nameChanged || tablesChanged || currentSlots !== originalSlots;
    }
  },
  methods: {
    addSlot () {
      this.localSlots.push(emptySlot());
    },
    format24to12 (time) {
      return format24to12(time);
    },
    removeSlot (index) {
      this.localSlots.splice(index, 1);
    },
    onEnter () {
      if (this.saving || !this.localName.trim() || this.localSlots.length === 0 || !this.hasChanges) return;
      this.save();
    },
    save () {
      if (this.saving || !this.location) return;
      this.saving = true;
      const slots = this.localSlots
        .filter(slot => slot.dayOfWeek != null && slot.time)
        .map(slot => ({
          dayOfWeek: slot.dayOfWeek,
          time: clockToMinutes(slot.time)
        }));
      this.location.immediateUpdate({
        name: this.localName.trim(),
        numberOfTables: this.localNumberOfTables,
        slots
      }).then(() => {
        this.$emit("pop-dialog");
      }).finally(() => {
        this.saving = false;
      });
    }
  }
};
</script>